/**
 * Product limits and tunables shared by server and clients.
 * Keep these in one place so the UI can explain limits the server enforces.
 */
import type { ChannelSettings, ChatThemePreset, GroupSettings, UserSettings } from './models.js';

export const APP_NAME = 'Enbox';

// ---------------------------------------------------------------------------
// Accounts & profiles
// ---------------------------------------------------------------------------

/**
 * Usernames: 3–32 chars of lowercase letters, digits, underscore and dot, with at least one
 * letter (so a login identifier made only of digits is always a phone number).
 * The `deleted_` prefix is reserved for deleted accounts (see `usernameSchema`).
 */
export const USERNAME_REGEX = /^(?=[a-z0-9_.]*[a-z])[a-z0-9_.]{3,32}$/;
/** Usernames of deleted accounts are rewritten to `deleted_<first 12 hex chars of the id>`. */
export const DELETED_USERNAME_PREFIX = 'deleted_';
export const DELETED_ACCOUNT_NAME = 'Deleted account';
export const PASSWORD_MIN_LENGTH = 8;
export const DISPLAY_NAME_MAX_LENGTH = 64;
export const ABOUT_MAX_LENGTH = 140;
export const DEFAULT_ABOUT = 'Hey there! I am using Enbox.';
/** Profile card "About me" (`bio`, multi-line). `about` stays the one-line status shown in chats. */
export const BIO_MAX_LENGTH = 190;
export const PRONOUNS_MAX_LENGTH = 40;
/** Custom presence note text (`PresenceNote.text`). */
export const PRESENCE_NOTE_MAX_LENGTH = 128;
/** A device reports `presence:activity { idle: true }` after this long without user input. */
export const PRESENCE_IDLE_AFTER_MS = 600_000;
/**
 * …or after this long with the page hidden: a quick tab switch or an occluded window
 * (Chrome reports it hidden) is not absence, and every transition costs a rate-limited event.
 */
export const PRESENCE_HIDDEN_IDLE_MS = 30_000;

/** User search (`GET /api/users/search`): username prefix matching needs this many chars. */
export const USER_SEARCH_MIN_PREFIX = 3;
/** Max results of `GET /api/users/search`. */
export const USER_SEARCH_LIMIT = 20;
/** Max ids per `POST /api/users/batch` / `POST /api/users/presence` / `presence:subscribe`. */
export const MAX_USERS_BATCH = 500;
/** Presence subscriptions are per socket; ids beyond this cap are ignored (no error). */
export const MAX_PRESENCE_SUBSCRIPTIONS = 1_000;
/** Max ids in each status privacy list (`statusExcludeUserIds`, `statusOnlyShareWithUserIds`). */
export const MAX_PRIVACY_LIST_SIZE = 5_000;

/**
 * Defaults for every user setting. `users.settings` stores only overrides: always read
 * settings through `resolveUserSettings()` (utils.ts), which merges them over these.
 */
export const DEFAULT_USER_SETTINGS: Readonly<UserSettings> = Object.freeze({
  lastSeenVisibility: 'everyone',
  onlineVisibility: 'everyone',
  profilePhotoVisibility: 'everyone',
  aboutVisibility: 'everyone',
  groupsAddPermission: 'everyone',
  readReceipts: true,
  silenceUnknownCallers: false,
  statusPrivacy: 'contacts',
  statusExcludeUserIds: [],
  statusOnlyShareWithUserIds: [],
  defaultDisappearingSeconds: null,
  messageNotifications: true,
  groupNotifications: true,
  callNotifications: true,
  notificationPreviews: true,
  friendRequestsFrom: 'everyone',
  whoCanDm: 'everyone',
  messageRequests: true,
  allowSpaceDms: true,
  reactionNotifications: 'dms',
});

// ---------------------------------------------------------------------------
// Chats & messages
// ---------------------------------------------------------------------------

export const MAX_MESSAGE_LENGTH = 65_536;
export const MAX_CAPTION_LENGTH = 4_096;
export const MAX_GROUP_NAME_LENGTH = 100;
export const MAX_DESCRIPTION_LENGTH = 2_048;

/**
 * Groups (and community announcement groups) can hold at most this many active members.
 * Since every community member is in the announcement group, this also caps communities.
 */
export const MAX_GROUP_MEMBERS = 1_024;
/** Communities can link at most this many groups (plus the announcement group). */
export const MAX_COMMUNITY_GROUPS = 100;

/** Group settings written on group creation (merged with the request's `settings`). */
export const DEFAULT_GROUP_SETTINGS: Readonly<GroupSettings> = Object.freeze({
  onlyAdminsCanSend: false,
  onlyAdminsCanEditInfo: false,
  onlyAdminsCanAddMembers: false,
});
/** Fixed settings of every community announcement group (never changeable). */
export const ANNOUNCEMENT_GROUP_SETTINGS: Readonly<GroupSettings> = Object.freeze({
  onlyAdminsCanSend: true,
  onlyAdminsCanEditInfo: true,
  onlyAdminsCanAddMembers: true,
});
/** Channel settings written on channel creation (merged with the request). */
export const DEFAULT_CHANNEL_SETTINGS: Readonly<ChannelSettings> = Object.freeze({
  isPublic: true,
  reactions: 'all',
});

/** A sender may edit a message within this window (channel admins: any post). */
export const EDIT_WINDOW_MS = 15 * 60 * 1000;
/**
 * A sender may delete a message for everyone within this window (60 hours, like WhatsApp's
 * ~2.5 days). Group admins and channel admins may delete any message at any time.
 */
export const DELETE_FOR_EVERYONE_WINDOW_MS = 60 * 60 * 60 * 1000;
/** Messages forwarded this many times or more are labelled "Forwarded many times". */
export const FORWARDED_MANY_TIMES_THRESHOLD = 5;
/** Max chats a message can be forwarded to in one action. */
export const MAX_FORWARD_TARGETS = 5;
/** Max messages forwarded in one action. */
export const MAX_FORWARD_MESSAGES = 50;
/** Max distinct users mentioned in one message (extra mention tokens are plain text). */
export const MAX_MENTIONS = 50;

export const MAX_PINNED_CHATS = 3;
export const MAX_PINNED_MESSAGES = 3;

/** Allowed disappearing-message timers (seconds). `null` means off. */
export const DISAPPEARING_OPTIONS = [24 * 60 * 60, 7 * 24 * 60 * 60, 90 * 24 * 60 * 60] as const;

export const QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🙏'] as const;
/** Max UTF-16 code units of a reaction emoji (one grapheme, see `emojiSchema`). */
export const MAX_EMOJI_LENGTH = 16;

export const POLL_QUESTION_MAX_LENGTH = 300;
export const POLL_MAX_OPTIONS = 12;
export const POLL_OPTION_MAX_LENGTH = 100;
/** Poll durations (Discord's choices: 1 h, 4 h, 8 h, 24 h, 3 days, 1 week, 2 weeks). */
export const POLL_DURATIONS_HOURS = [1, 4, 8, 24, 72, 168, 336] as const;
export const DEFAULT_POLL_DURATION_HOURS = 24;
/** `ForwardRequest.comment`: sent as a normal text message after the copies, in each chat. */
export const FORWARD_COMMENT_MAX_LENGTH = 2000;

/** Default page size for message history. */
export const MESSAGES_PAGE_SIZE = 50;
/** Max `limit` of a message page. */
export const MAX_MESSAGES_PAGE_SIZE = 200;
export const SEARCH_RESULTS_LIMIT = 50;
/** Longest link `GET /api/link-preview` accepts. */
export const MAX_LINK_PREVIEW_URL_LENGTH = 2_048;

/** A far-future timestamp used for "mute always". */
export const MUTE_FOREVER_ISO = '9999-12-31T23:59:59.000Z';

/** Typing indicators expire client-side after this long without a refresh. */
export const TYPING_TIMEOUT_MS = 6_000;
/** While the user keeps typing/recording, clients re-send the state this often. */
export const TYPING_REFRESH_MS = 3_000;

// ---------------------------------------------------------------------------
// Chat themes (`ChatTheme`, models.ts)
// ---------------------------------------------------------------------------

/**
 * A theme is enum ids plus one validated `#rrggbb` accent — never CSS or URLs — so a stored
 * theme can be applied as CSS variables without escaping (`chatThemeSchema`).
 */
export const CHAT_THEME_PRESETS = [
  'default',
  'midnight',
  'ocean',
  'forest',
  'sunset',
  'rose',
  'mono',
  'lavender',
] as const;
/** Display names of the presets (system messages, pickers). */
export const CHAT_THEME_PRESET_LABELS: Readonly<Record<ChatThemePreset, string>> = Object.freeze({
  default: 'Default',
  midnight: 'Midnight',
  ocean: 'Ocean',
  forest: 'Forest',
  sunset: 'Sunset',
  rose: 'Rose',
  mono: 'Mono',
  lavender: 'Lavender',
});
/** `cozy` = Discord-style rows (avatar + name header, no bubble). */
export const BUBBLE_STYLES = ['classic', 'rounded', 'minimal', 'cozy'] as const;
/** Enter animation of newly arriving messages (clamped off under reduced motion). */
export const MESSAGE_ANIMATIONS = ['none', 'fade', 'slide', 'pop'] as const;
/**
 * Built-in wallpapers: the flat colours (light/dark pair per id, `default` = the theme's
 * own token) and the animated ones (`aurora`, `drift`, `starfield`, `waves`: transform/
 * opacity keyframes, shown static under reduced motion).
 */
export const WALLPAPER_PRESETS = [
  'default',
  'lavender',
  'sky',
  'mint',
  'sand',
  'rose',
  'slate',
  'aurora',
  'drift',
  'starfield',
  'waves',
] as const;
/** `ChatTheme.dim`: darkening overlay over the wallpaper, percent. */
export const WALLPAPER_DIM_MAX = 80;
/** `ChatTheme.blur`: backdrop blur of the wallpaper, px. */
export const WALLPAPER_BLUR_MAX = 20;

// ---------------------------------------------------------------------------
// Invites
// ---------------------------------------------------------------------------

/** Alphabet of invite codes (URL-safe, no look-alike characters). */
// ---------------------------------------------------------------------------
// Spaces (docs/design/spaces-at-scale.md)
// ---------------------------------------------------------------------------

export const MAX_SPACES_PER_USER = 200;
export const MAX_SPACE_MEMBERS = 250_000;
/** Text + voice channels per Space. */
export const MAX_SPACE_CHANNELS = 500;
export const MAX_SPACE_CATEGORIES = 50;
/** Roles per Space, including @everyone. */
export const MAX_SPACE_ROLES = 250;
export const MAX_OVERWRITES_PER_CHANNEL = 100;
export const MAX_SPACE_INVITES = 1_000;
/**
 * Above this many members a Space is "large": member events only reach sockets with the member
 * list open, and the member list is alphabetical (paged) instead of grouped by presence.
 */
export const LARGE_SPACE_THRESHOLD = 1_000;
export const SPACE_NAME_MAX_LENGTH = 100;
export const SPACE_DESCRIPTION_MAX_LENGTH = 1_000;
export const CHANNEL_NAME_MAX_LENGTH = 100;
export const CHANNEL_TOPIC_MAX_LENGTH = 1_024;
export const ROLE_NAME_MAX_LENGTH = 100;
export const NICKNAME_MAX_LENGTH = 32;
export const BAN_REASON_MAX_LENGTH = 512;
export const MAX_VOICE_CHANNEL_PARTICIPANTS = 99;
/** Seconds between messages per member (0 = off). */
export const SLOWMODE_OPTIONS = [
  0, 5, 10, 15, 30, 60, 120, 300, 600, 900, 1800, 3600, 7200, 21600,
] as const;
/** Longest timeout (Discord: 28 days). */
export const MAX_TIMEOUT_MS = 28 * 24 * 60 * 60_000;
/** Invite lifetimes offered (seconds, 0 = never expires). */
export const INVITE_MAX_AGE_OPTIONS = [0, 1800, 3600, 21600, 43200, 86400, 604800] as const;
/** Invite use caps offered (0 = unlimited). */
export const INVITE_MAX_USES_OPTIONS = [0, 1, 5, 10, 25, 50, 100] as const;
/** Ban: delete the banned member's messages of the last N seconds (bounded purge). */
export const BAN_DELETE_MESSAGE_OPTIONS = [0, 3600, 86400, 604800] as const;
export const MAX_PINNED_MESSAGES_SPACE = 250;
export const MAX_SPACE_MEMBERS_PAGE = 200;
/** Unread and mention counts of space channels are capped (badges show "99+"). */
export const SPACE_UNREAD_CAP = 100;
/** @everyone/@here pushes per message are capped (the rest still see the mention badge). */
export const PUSH_SPACE_ALL_CAP = 5_000;
export const AUDIT_LOG_RETENTION_DAYS = 90;
export const MAX_AUDIT_LOG_PAGE = 100;

export const INVITE_CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789';
/** Invite codes are this many characters (≈ 128 bits). */
export const INVITE_CODE_LENGTH = 22;

// ---------------------------------------------------------------------------
// Status updates (stories)
// ---------------------------------------------------------------------------

/** Status updates (stories) disappear after 24h. */
export const STATUS_TTL_MS = 24 * 60 * 60 * 1000;
export const STATUS_TEXT_MAX_LENGTH = 700;
export const STATUS_BACKGROUND_COLORS = [
  '#6D5DFC',
  '#0EA5E9',
  '#10B981',
  '#F59E0B',
  '#EF4444',
  '#EC4899',
  '#8B5CF6',
  '#14B8A6',
  '#334155',
  '#A16207',
] as const;
export const STATUS_FONT_COUNT = 5;

// ---------------------------------------------------------------------------
// Calls
// ---------------------------------------------------------------------------

/**
 * Group/1:1 calls use a full-mesh WebRTC topology, which caps participants (joined users,
 * caller included). A call rings at most `MAX_CALL_PARTICIPANTS - 1` users.
 */
export const MAX_CALL_PARTICIPANTS = 8;
/** How long an invitee rings before becoming `missed`. */
export const CALL_RING_TIMEOUT_MS = 45_000;
/**
 * A joined participant whose call socket disconnected may reclaim the call with
 * `call:rejoin` within this window; afterwards they are marked `left`.
 */
export const CALL_RECONNECT_GRACE_MS = 20_000;

// ---------------------------------------------------------------------------
// Media
// ---------------------------------------------------------------------------

/** Upload limits in bytes. */
export const MAX_UPLOAD_BYTES = 100 * 1024 * 1024;
/**
 * Kind `image` uploads. The server parses an image whole when its head does not settle it
 * and always rewrites it whole without metadata, so this bounds what it reads into memory
 * (400 above it; a larger photo goes as a document — kind `file` — which is neither parsed
 * nor rendered inline).
 */
export const MAX_IMAGE_BYTES = 32 * 1024 * 1024;
export const MAX_AVATAR_BYTES = 5 * 1024 * 1024;
/** Optional client-generated thumbnail/poster (multipart `thumbnail`). */
export const MAX_THUMBNAIL_BYTES = 200 * 1024;
export const THUMBNAIL_MIME_TYPES = ['image/jpeg', 'image/webp'] as const;
/** Voice-note waveform: at most this many 0..1 samples. */
export const WAVEFORM_MAX_SAMPLES = 64;
/** Sanitised file names are truncated to this many characters. */
export const MAX_FILE_NAME_LENGTH = 200;
/** Clients re-encode photos so the longest side is at most this (strips EXIF). */
export const IMAGE_MAX_DIMENSION = 2_560;
/** Clients re-encode avatars so the longest side is at most this. */
export const AVATAR_MAX_DIMENSION = 640;
/** Raster types accepted as static avatars (user, group, community, channel). */
export const AVATAR_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;
/**
 * Animated avatars (GIF, animated WebP, APNG — `MediaAttachment.animated`) may be larger
 * than static ones and MUST carry a static poster (multipart `thumbnail`): `avatarUrl` is
 * always the static image, `avatarAnimatedUrl` the animation.
 */
export const MAX_ANIMATED_AVATAR_BYTES = 8 * 1024 * 1024;
/** Raster types that can be animated (APNG sniffs as `image/png`). Never AVIF for profile media. */
export const ANIMATED_IMAGE_MIME_TYPES = ['image/gif', 'image/webp', 'image/png'] as const;
/** Profile banners: static or animated (same poster rule as avatars), cropped to BANNER_ASPECT. */
export const MAX_BANNER_BYTES = 10 * 1024 * 1024;
export const BANNER_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'] as const;
/** Banner aspect ratio, width : height. */
export const BANNER_ASPECT = [5, 2] as const;
/**
 * Custom chat wallpapers (`chat_members.wallpaper_media_id`, viewer-private): an image
 * (WALLPAPER_IMAGE_MIME_TYPES, ≤ MAX_WALLPAPER_BYTES) or a short muted video
 * (WALLPAPER_VIDEO_MIME_TYPES, ≤ MAX_WALLPAPER_VIDEO_BYTES and MAX_WALLPAPER_VIDEO_MS).
 * Animated images and videos MUST carry a static poster (multipart `thumbnail`), shown under
 * reduced motion and while the app is hidden. `maxWallpaperBytes` is in `GET /api/config.limits`.
 */
export const MAX_WALLPAPER_BYTES = 15 * 1024 * 1024;
export const MAX_WALLPAPER_VIDEO_BYTES = 25 * 1024 * 1024;
export const MAX_WALLPAPER_VIDEO_MS = 30_000;
export const WALLPAPER_IMAGE_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
] as const;
export const WALLPAPER_VIDEO_MIME_TYPES = ['video/mp4', 'video/webm'] as const;
/**
 * Server-side image checks (`POST /api/media`, kind image; shared `readImageInfo`): a side
 * above IMAGE_HEADER_MAX_DIMENSION, more than ANIMATED_MAX_FRAMES frames, or
 * width × height × frames above ANIMATED_DECODED_PIXEL_BUDGET → 400.
 */
export const IMAGE_HEADER_MAX_DIMENSION = 8192;
export const ANIMATED_MAX_FRAMES = 400;
export const ANIMATED_DECODED_PIXEL_BUDGET = 200_000_000;
/** Bytes a client sniffs (`readImageInfo`) to detect type, dimensions and animation. */
export const IMAGE_PROBE_BYTES = 65_536;
/** Unreferenced media rows/files older than this are garbage-collected. */
export const ORPHAN_MEDIA_TTL_MS = 24 * 60 * 60 * 1000;

/**
 * Sniffed MIME types accepted per upload kind. `null` = anything (served as an attachment).
 * SVG is never accepted as an image, and neither is AVIF: the image kind allows exactly the
 * types the shared parser reads (`readImageInfo`), so every image upload is verified and
 * stripped before it is stored — an AVIF goes as a file. Audio-only WebM/MP4 files sniff as
 * `video/*`, so the audio and voice kinds accept those containers too.
 */
export const MEDIA_MIME_ALLOWLIST = {
  image: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
  video: ['video/mp4', 'video/webm', 'video/quicktime'],
  audio: [
    'audio/mpeg',
    'audio/mp4',
    'audio/aac',
    'audio/ogg',
    'audio/webm',
    'audio/wav',
    'audio/opus',
    'video/webm',
    'video/mp4',
  ],
  voice: [
    'audio/mpeg',
    'audio/mp4',
    'audio/aac',
    'audio/ogg',
    'audio/webm',
    'audio/wav',
    'audio/opus',
    'video/webm',
    'video/mp4',
  ],
  file: null,
} as const satisfies Record<
  'image' | 'video' | 'audio' | 'voice' | 'file',
  readonly string[] | null
>;

// ---------------------------------------------------------------------------
// Web push
// ---------------------------------------------------------------------------

/** Push endpoints must be https on one of these hosts ... */
export const PUSH_SERVICE_HOSTS = [
  'fcm.googleapis.com',
  'updates.push.services.mozilla.com',
  'web.push.apple.com',
] as const;
/** ... or on a subdomain of one of these. */
export const PUSH_SERVICE_HOST_SUFFIXES = [
  '.notify.windows.com',
  '.push.apple.com',
  '.push.services.mozilla.com',
] as const;
/** TTL of message pushes (seconds). Call pushes use CALL_RING_TIMEOUT_MS / 1000. */
export const PUSH_MESSAGE_TTL_SEC = 24 * 60 * 60;

// ---------------------------------------------------------------------------
// Per-user / per-socket rate limits (server: `limitUser()` in lib/userLimit.ts)
// ---------------------------------------------------------------------------

/**
 * `limit` actions per `windowMs`. Exceeding one returns 429 `rate_limited` (acks: `{ ok:false }`),
 * except `typing` and `presenceActivity` (no ack), which are dropped silently. Per-IP limits
 * (auth, invites, uploads, general API) are separate (server lib/rateLimit.ts).
 */
export const USER_RATE_LIMITS = {
  /** Message sends and forwards, per user. */
  sendMessage: { limit: 60, windowMs: 10_000 },
  /** Reaction adds, removals and clears (`/api/messages/:messageId/reaction[s]…`), per user. */
  reaction: { limit: 40, windowMs: 10_000 },
  /** Super reactions added (they animate for everyone in the chat), per user. */
  superReaction: { limit: 30, windowMs: 60 * 60_000 },
  /** `PATCH /me`, `PUT /me/presence`, `PUT|DELETE /me/presence-note`, per user. */
  profileUpdate: { limit: 20, windowMs: 60_000 },
  /** Users added to groups/communities (counted per added user), per user. */
  addMembers: { limit: 200, windowMs: 60 * 60_000 },
  /** `call:start`, per user. */
  callStart: { limit: 10, windowMs: 60_000 },
  /** `voice:join`, per user. */
  voiceJoin: { limit: 20, windowMs: 60_000 },
  /** `voice:update`, per socket. */
  voiceUpdate: { limit: 30, windowMs: 10_000 },
  /** `chat:typing`, per socket and chat. */
  typing: { limit: 1, windowMs: 1_000 },
  /** `presence:subscribe` events, per socket. */
  presenceSubscribe: { limit: 30, windowMs: 60_000 },
  /** `presence:activity` events, per socket. */
  presenceActivity: { limit: 30, windowMs: 60_000 },
  /** `GET /api/users/search` and `POST /api/contacts`, per user. */
  userSearch: { limit: 60, windowMs: 60_000 },
  /** Message search (`GET /api/spaces/:spaceId/messages/search`, `/api/chats/:chatId/messages/search`), per user. */
  search: { limit: 30, windowMs: 60_000 },
  /** `PUT /api/me/rail-layout` (drag-reordering Spaces and folders), per user. */
  railLayout: { limit: 60, windowMs: 60_000 },
  /** `PUT /api/users/:userId/note` (private notes; saved on blur), per user. */
  userNote: { limit: 60, windowMs: 60_000 },
  /** `POST /api/friends/requests` (failed lookups count too: no username probing), per user. */
  friendRequest: { limit: 30, windowMs: 60 * 60_000 },
  /** `PUT /api/chats/:chatId/theme` (shared theme changes, each a system message), per user. */
  chatTheme: { limit: 20, windowMs: 60_000 },
  /** `POST /api/spaces`, per user. */
  spaceCreate: { limit: 10, windowMs: 60 * 60_000 },
  /** Joining Spaces through invites, per user. */
  spaceJoin: { limit: 30, windowMs: 60 * 60_000 },
  /** Channel and category create/edit/delete/reorder, per user. */
  spaceChannelUpdate: { limit: 120, windowMs: 60 * 60_000 },
  /** Role and overwrite create/edit/delete/reorder, per user. */
  spaceRoleUpdate: { limit: 120, windowMs: 60 * 60_000 },
  /** Member edits (nickname, roles, timeout), kicks and bans, per user. */
  spaceMemberUpdate: { limit: 300, windowMs: 60 * 60_000 },
  /** Invite links created, per user. */
  spaceInviteCreate: { limit: 30, windowMs: 60 * 60_000 },
  /** Messages mentioning @everyone / @here, per user. */
  mentionEveryone: { limit: 5, windowMs: 60_000 },
} as const;

// ---------------------------------------------------------------------------
// Voice channels and Space options
// ---------------------------------------------------------------------------

/** Default and maximum voice channel bitrate (bit/s). */
export const VOICE_DEFAULT_BITRATE = 64_000;
export const VOICE_MIN_BITRATE = 8_000;
export const VOICE_MAX_BITRATE = 96_000;
/** Max people in one voice channel without a user limit (media server load). */
export const VOICE_MAX_PARTICIPANTS = 99;
/** LiveKit tokens live this long; joining again mints a new one. */
export const VOICE_TOKEN_TTL_SEC = 10 * 60;

/** `Space.systemFlags`: system messages that are NOT posted to the system channel. */
export const SPACE_SYSTEM_FLAGS = {
  SUPPRESS_JOIN_NOTIFICATIONS: 1 << 0,
  SUPPRESS_JOIN_REPLIES: 1 << 1,
  SUPPRESS_SETUP_TIPS: 1 << 2,
} as const;

/** `Space.verificationLevel` labels (Discord's five levels). */
export const SPACE_VERIFICATION_LEVELS = [
  { level: 0, label: 'None', description: 'Unrestricted' },
  { level: 1, label: 'Low', description: 'Must have a verified email on their account' },
  { level: 2, label: 'Medium', description: 'Must also be registered on Enbox for longer than 5 minutes' },
  { level: 3, label: 'High', description: 'Must also be a member of this Space for longer than 10 minutes' },
  { level: 4, label: 'Highest', description: 'Must have a verified phone number' },
] as const;

/** Discord's mute durations (ms; 0 = until I turn it back on). */
export const SPACE_MUTE_DURATIONS = [
  { label: 'For 15 minutes', ms: 15 * 60_000 },
  { label: 'For 1 hour', ms: 3_600_000 },
  { label: 'For 3 hours', ms: 3 * 3_600_000 },
  { label: 'For 8 hours', ms: 8 * 3_600_000 },
  { label: 'For 24 hours', ms: 24 * 3_600_000 },
  { label: 'Until I turn it back on', ms: 0 },
] as const;

/** Cap on each mutuals list of a full profile. */
export const MUTUALS_LIMIT = 100;
/** Private note about a user, in characters. */
export const USER_NOTE_MAX_LENGTH = 256;
